"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  type HTMLMotionProps,
  type Variants,
} from "framer-motion";

// Slightly tighter than the first pass (y18/blur6/0.6s vs y24/blur8/
// 0.65s) — the shorter travel reads as precision rather than drift.
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 18, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export const stagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.05,
    },
  },
};

export const floatSlow: Variants = {
  animate: {
    y: [-6, 6, -6],
    transition: {
      duration: 7,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

type DivProps = HTMLMotionProps<"div">;

export function MotionGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={reduceMotion ? undefined : stagger}
      initial={reduceMotion ? false : "hidden"}
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Reveal({
  children,
  className,
  once = true,
  ...props
}: DivProps & { once?: boolean }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={reduceMotion ? undefined : fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="visible"
      viewport={{ once, amount: 0.2 }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * Hero entrance — pure CSS (see globals.css .hero-rise). Unlike the
 * framer-motion version this renders VISIBLE-and-animating HTML from
 * the server, so the hero never sits blank waiting for hydration.
 * Reduced-motion is handled by the stylesheet's media query.
 */
export function HeroItem({
  children,
  className,
  delay = 0,
  fromRight = false,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  fromRight?: boolean;
}) {
  return (
    <div
      className={`${fromRight ? "hero-rise-right" : "hero-rise"} ${className ?? ""}`}
      style={{ animationDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

export function FloatSlow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={reduceMotion ? undefined : floatSlow}
      animate={reduceMotion ? undefined : "animate"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Pointer-tracking 3D tilt for the hero phone stage. Mouse only —
 * touch gets the float alone — and capped at a few degrees so it
 * reads as depth, not a gimmick. Springs give it physical settle.
 */
export function TiltStage({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const springRx = useSpring(rx, { stiffness: 110, damping: 16 });
  const springRy = useSpring(ry, { stiffness: 110, damping: 16 });

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      style={{
        rotateX: springRx,
        rotateY: springRy,
        transformPerspective: 1200,
        willChange: "transform",
      }}
      onPointerMove={(e) => {
        if (e.pointerType !== "mouse") return;
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        ry.set(px * 6);
        rx.set(py * -4.5);
      }}
      onPointerLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
    >
      {children}
    </motion.div>
  );
}

/**
 * Stagger-participating list row — for hairline lists where the card
 * treatment (lift + glow shadow) would be too heavy. Hover is a quiet
 * 3px x-nudge instead.
 */
export function MotionRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={reduceMotion ? undefined : fadeUp}
      whileHover={reduceMotion ? undefined : { x: 3 }}
      transition={{ type: "spring", stiffness: 300, damping: 24 }}
      className={`transition-colors duration-200 ${className ?? ""}`}
    >
      {children}
    </motion.div>
  );
}

export function PremiumMotionCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      variants={reduceMotion ? undefined : fadeUp}
      whileHover={reduceMotion ? undefined : { y: -4 }}
      whileTap={reduceMotion ? undefined : { scale: 0.99 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      // Border + glow bloom on hover rides CSS so it composes with the
      // per-call-site hover:border-* tints; the lift rides the spring.
      className={`transition-[border-color,box-shadow,background-color] duration-300 hover:shadow-[0_22px_60px_rgba(0,0,0,0.4),0_6px_36px_rgba(242,105,96,0.09)] ${className ?? ""}`}
    >
      {children}
    </motion.div>
  );
}

export function StickyHeader({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`${className ?? ""} ${
        scrolled
          ? "is-scrolled border-white/[0.08] bg-[#080808]/90 shadow-[0_16px_50px_rgba(0,0,0,0.28)]"
          : "border-transparent bg-[#080808]/72"
      }`}
    >
      {children}
    </header>
  );
}

/**
 * Counts up when it scrolls INTO VIEW — not on mount. The stats live
 * far below the fold; a mount-triggered count finishes long before
 * anyone sees it, which is why the numbers used to look static.
 */
export function CountUp({
  value,
  prefix = "",
  suffix = "",
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) {
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (reduceMotion || !inView) return;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / 1100);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion, inView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {(reduceMotion ? value : display).toLocaleString()}
      {suffix}
    </span>
  );
}
